/** @jsxImportSource @emotion/react */
import { OrganizationKeyName } from "../../../model/Organization";
import { UserProfileDataResponse } from "../../../service/api-response";
import { RootState } from "../../../store";
import * as authSelectors from "../../auth/authSelectors";
import * as organizationSelectors from "../../organization/organizationSelectors";
import { UpdateUserData } from "../../../service/ApiUser";
import { EditableDetailsRow } from "../../../components/EditableDetailsRow";
import { CardHeader } from "../../../components/CardHeader";
import { DetailsRow } from "../../../components/DetailsRow";
import { DeleteButton } from "../../../components/NewButtons";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../appRoutingHelpers";
import {
  useAnonymizeUser,
  useUpdateUser,
  useUpdateUserTermConsent,
  useUserTerms,
} from "../../../service/api";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";
import { formatDateWithYear, formatDateWithYearAndTime } from "../../../util";
import { css } from "@emotion/react";
import { StyledText } from "../../../assets/typography/typography";
import { SwitchField } from "../../../components/forms/fields/SwitchField";
import { SpinnerBlock } from "../../common/Spinner";
import { http } from "../../../service/http";
import { colors } from "../../../components/colors";

interface Props {
  userProfile: UserProfileDataResponse;
}

const acceptTermsWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  width: 100%;
`;

const termWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 16px;
`;

const termAcceptedTextStyle = css`
  white-space: nowrap;
`;

const UserProfileDetailsPartial = ({ userProfile }: Props) => {
  const navigate = useNavigate();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const isEmailFieldVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserProfileFieldVisible(state, "email")
  );
  const isPhoneNbFieldVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserProfileFieldVisible(state, "phoneNb")
  );
  const isUserIdFieldVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserProfileFieldVisible(state, "userId")
  );
  const isFacebookFieldVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserProfileFieldVisible(state, "facebook")
  );
  const isGenderFieldVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserProfileFieldVisible(state, "gender")
  );

  const { mutateAsync: updateUser } = useUpdateUser(userProfile.id);
  const { mutateAsync: anonymize } = useAnonymizeUser();

  const { data: userTerms, isLoading: isLoadingUserTerms } = useUserTerms({
    organizationKeyName,
    enabled: true,
  });

  const {
    mutateAsync: updateUserTermConsent,
    isLoading: isLoadingUpdateUserTermConsent,
  } = useUpdateUserTermConsent({
    organizationKeyName,
    id: userProfile.id,
  });

  const updateUserAttr = async (
    org: OrganizationKeyName,
    userId: string,
    data: UpdateUserData
  ) => {
    try {
      await updateUser({
        data,
        organizationKeyName: org,
        userId,
      });
      toastr.success("Success", "User updated successfully");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const anonymizeUser = async (org: OrganizationKeyName, userId: string) => {
    if (
      !window.confirm(
        `OBS: Remember to delete user in Mixpanel first 🛑\nYou are about to delete this user. This cannot be un-done.\n\nDo you want to proceed?`
      )
    ) {
      return;
    }
    try {
      await anonymize({
        organizationKeyName,
        userId,
      });
      toastr.success("Success", "User deleted successfully");
      navigate(routes.userList(organizationKeyName));
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const onInlineEdit = async (
    fieldname: string,
    value: string
  ): Promise<boolean> => {
    if (fieldname === "email") {
      var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const isValid = re.test(value);
      if (!isValid) {
        alert("Invalid email address");
        return false;
      }
    }

    await updateUserAttr(organizationKeyName, userProfile.id, {
      [fieldname]: value,
    });

    return true;
  };

  return (
    <>
      {(isEmailFieldVisible ||
        isPhoneNbFieldVisible ||
        isUserIdFieldVisible ||
        isFacebookFieldVisible ||
        isGenderFieldVisible) && (
        <>
          <div className="card">
            <CardHeader title="User details">
              <DeleteButton
                type="button"
                onClick={async (e) => {
                  e.preventDefault();
                  await anonymizeUser(
                    organizationKeyName,
                    userProfile.userExternalId
                  );
                }}
              >
                Delete
              </DeleteButton>
            </CardHeader>
            <div
              className="card-body d-flex flex-column"
              style={{ gap: "16px" }}
            >
              <DetailsRow leftText="User id" rightText={userProfile.id} />
              <EditableDetailsRow
                leftText="First name"
                defaultValue={userProfile.firstName}
                onlineEdit={onInlineEdit}
                fieldName="firstName"
              />
              <EditableDetailsRow
                leftText="Last name"
                onlineEdit={onInlineEdit}
                defaultValue={userProfile.lastName}
                fieldName="lastName"
              />
              <EditableDetailsRow
                leftText="Email"
                onlineEdit={onInlineEdit}
                defaultValue={userProfile.email}
                fieldName="email"
              />
              <EditableDetailsRow
                leftText="Phone number"
                defaultValue={userProfile.phoneNumber}
                fieldName="phoneNumber"
                onlineEdit={onInlineEdit}
              />
              <EditableDetailsRow
                leftText="Gender"
                type="select"
                defaultValue={userProfile.gender as string}
                selectOptions={[
                  { label: "male", value: "male" },
                  { label: "female", value: "female" },
                  { label: "not specified", value: "" },
                ]}
                fieldName="gender"
                onlineEdit={onInlineEdit}
              />
              <EditableDetailsRow
                leftText="Default payment gateway"
                type="select"
                selectOptions={[
                  { label: "transpayrent", value: "transpayrent" },
                  { label: "stripe", value: "stripe" },
                  { label: "reepay", value: "reepay" },
                ]}
                defaultValue={userProfile.defaultGateway as string}
                onlineEdit={onInlineEdit}
                fieldName="defaultGateway"
              />
              <DetailsRow
                leftText="Facebook"
                rightText={userProfile.facebook}
              />
              <DetailsRow leftText="Address" rightText={userProfile.address} />
              <DetailsRow
                leftText="Postcode"
                rightText={userProfile.postcode}
              />
              <DetailsRow leftText="Country" rightText={userProfile.country} />
              {userProfile.birthday && (
                <DetailsRow
                  leftText="Birthday"
                  rightText={formatDateWithYear(userProfile.birthday)}
                />
              )}
              {userProfile.favoriteRestaurant && (
                <DetailsRow
                  leftText="Favorite restaurant"
                  rightText={userProfile.favoriteRestaurant.title}
                />
              )}
            </div>
          </div>
        </>
      )}

      <div className="card">
        <CardHeader title="Terms" />
        <div className="card-body d-flex flex-column" style={{ gap: "16px" }}>
          {isLoadingUserTerms ? (
            <SpinnerBlock />
          ) : (
            <>
              {userTerms?.map((term, index) => {
                const acceptedTerm = userProfile.acceptedTerms?.find(
                  (t) => t.termId === term.id
                );
                // Terms & conditions cannot be mutated. So we disable the switch if it is the id of the terms & conditions.
                const isDisabled =
                  isLoadingUpdateUserTermConsent ||
                  term.id === "0191903c-74b3-70c0-be32-ceaf64fcb766";

                return (
                  <div css={termWrapperStyle} key={index}>
                    <StyledText
                      className="bodyTextRegular mb-0"
                      color={colors.textPrimary}
                    >
                      {term.title}
                    </StyledText>
                    <div css={acceptTermsWrapperStyle}>
                      <SwitchField
                        checked={!!acceptedTerm}
                        disabled={isDisabled}
                        onChange={async (e) => {
                          if (
                            !window.confirm(
                              `You are about to update the user term consent.\n\nDo you want to proceed? \n\nOBS: This also updates the user term consent in Mixpanel, so you do NOT need to do that manually. 🛑`
                            )
                          ) {
                            return;
                          }
                          const checked = e.target.checked;
                          try {
                            let versionId = acceptedTerm?.versionId;

                            if (!versionId) {
                              const userTermVersions = await http.getUserTermVersions(
                                {
                                  organizationKeyName,
                                  termId: term.id,
                                }
                              );
                              const sortedUserTermVersions = userTermVersions.sort(
                                (a, b) => b.version - a.version
                              );
                              versionId = sortedUserTermVersions[0].id;
                            }

                            await updateUserTermConsent({
                              termId: versionId,
                              status: checked ? "1" : "0",
                              organizationKeyName,
                              userId: userProfile.id,
                            });
                            toastr.success(
                              "Success",
                              "User term consent updated successfully"
                            );
                          } catch (error) {
                            const errorMessage = getErrorMessage(error);
                            toastr.error("Error", errorMessage);
                          }
                        }}
                      />
                      <StyledText
                        className="bodySubtextRegular mb-0"
                        css={termAcceptedTextStyle}
                        color={colors.textSecondary}
                      >
                        {acceptedTerm
                          ? `Accepted (${formatDateWithYearAndTime(
                              acceptedTerm.acceptedAt
                            )})`
                          : "Not accepted"}
                      </StyledText>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {userProfile.familyMembers && userProfile.familyMembers.length > 0 && (
        <div className="card">
          <CardHeader title="Family birthdays" />
          <div className="card-body d-flex flex-column" style={{ gap: "16px" }}>
            {userProfile.familyMembers.map((member, index) => (
              <DetailsRow
                key={index}
                leftText={member.name}
                rightText={formatDateWithYear(member.birthday)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default withTranslation()(UserProfileDetailsPartial);
